import { useEffect, useState } from 'react'
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Logo } from './Logo'
import { Icon } from './Icon'
import { LanguageSwitcher } from './LanguageSwitcher'
import { useAuth } from '../auth/AuthContext'
import { languageFromPath, withLang } from '../i18n/i18n'

export function Nav() {
  const { user, loading, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const lang = languageFromPath(location.pathname)
  const [menuOpen, setMenuOpen] = useState(false)

  const items = [
    { to: withLang('/', lang), label: t('nav.home'), end: true },
    { to: withLang('/destinations', lang), label: t('nav.destinations'), end: false },
    { to: withLang('/how-it-works', lang), label: t('nav.howItWorks'), end: false },
    { to: withLang('/faq', lang), label: t('nav.faq'), end: false },
  ]

  // Close the mobile menu on any route change (link tap, browser back, etc.)
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    if (!menuOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [menuOpen])

  const doLogout = async () => {
    await logout()
    navigate(withLang('/', lang))
  }

  // Auth button cluster — rendered in both the desktop nav-right and the
  // mobile dropdown, so the user/guest logic lives in one place.
  const authActions = (onNavigate?: () => void) =>
    loading ? null : user ? (
      <>
        <Link to={withLang('/account', lang)} className="btn ghost sm reset" onClick={onNavigate}>
          {t('nav.myEsims')}
        </Link>
        <button
          className="btn subtle sm"
          onClick={() => {
            onNavigate?.()
            void doLogout()
          }}
        >
          {t('nav.signOut')}
        </button>
      </>
    ) : (
      <>
        <Link to={withLang('/login', lang)} className="btn ghost sm reset" onClick={onNavigate}>
          {t('nav.logIn')}
        </Link>
        <Link to={withLang('/destinations', lang)} className="btn primary sm reset" onClick={onNavigate}>
          {t('nav.getEsim')}
        </Link>
      </>
    )

  return (
    <nav className="nav">
      <div className="nav-inner">
        <Link to={withLang('/', lang)} aria-label="Nimvoy home">
          <Logo />
        </Link>
        <div className="nav-links">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) => (isActive ? 'active' : '')}
            >
              {it.label}
            </NavLink>
          ))}
        </div>
        <div className="nav-right">
          <LanguageSwitcher variant="nav" />
          {authActions()}
        </div>

        {/* Language switcher in the bar on phones (CSS-gated) — pulled out of
            the dropdown so it's reachable without opening the menu, matching
            desktop. */}
        <div className="nav-lang-mobile">
          <LanguageSwitcher variant="nav" />
        </div>

        {/* Hamburger — phones only (CSS-gated). Opens the dropdown below. */}
        <button
          type="button"
          className="nav-burger"
          aria-label="Menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <Icon name={menuOpen ? 'x' : 'menu'} size={22} />
        </button>
      </div>

      {menuOpen && (
        <div className="nav-mobile" role="menu">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              role="menuitem"
              className={({ isActive }) => `nav-mobile-link${isActive ? ' active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              {it.label}
            </NavLink>
          ))}
          <div className="nav-mobile-actions">
            {authActions(() => setMenuOpen(false))}
          </div>
        </div>
      )}
    </nav>
  )
}
